import { Flex, Text, useDisclosure, Tooltip, Code } from "@chakra-ui/react";
import { WarningIcon } from "@chakra-ui/icons";
import { BaseTable } from "@/components/BaseTable/BaseTable";
import { BaseTableFilters } from "@/components/BaseTableFilters/BaseTableFilters";
import { CellContext, ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Layout } from "@/components/Layout/Layout";
import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { RootState, useAppDispatch, useAppSelector } from "@/store";
import { fetchAllCandidates } from "@/store/slices/candidates";
import { ISingleCandidate } from "@/api/candidate/types";
import { formatDate } from "@/helpers/formatDate";
import { skillsSearchingFn } from "@/helpers/SkillsSortingFn";
import { Pagination } from "@/components/Pagination/Pagination";
import { useSorting } from "@/hooks/useSorting";
import api from "@/api";
import { transformGuides } from "@/helpers/transformGuides";
import { StatusCell } from "@/components/StatusCell/StatusCell";
import { BaseSelect } from "@/components/Controls/BaseSelect";
import { CandidateFilters } from "@/model/candidate";
import { FiltersPopover } from "./components/FiltersPopover";
import { updateRouteQuery } from "@/utils/route";
import { Recruiter } from "@/model/recruiters";
import { useSessionStorageFilters } from "@/hooks/useSessionStorageFilters";
import { defaultCandidateFilterFields } from "./_helpers";

const columnHelper = createColumnHelper<ISingleCandidate>();

// Функция для проверки, создан ли кандидат автоматически с сайта
const isAutoCreatedCandidate = (candidate: ISingleCandidate): boolean => {
  const hasNotSpecified =
    candidate.last_name?.toLowerCase().includes("не указан") ||
    candidate.first_name?.toLowerCase().includes("не указан") ||
    candidate.patronymic_name?.toLowerCase().includes("не указан") ||
    candidate.expected_position?.toLowerCase().includes("не указан") ||
    candidate.contact_email?.toLowerCase().includes("не указан") ||
    candidate.contact_phone?.toLowerCase().includes("не указан") ||
    candidate.contact_telegram?.toLowerCase().includes("не указан");

  const isFromWebsite = candidate.source?.name?.toLowerCase() === "сайт";

  return hasNotSpecified && isFromWebsite;
};

const SkillsCell = (info: CellContext<ISingleCandidate, string>) => {
  const skills = info?.row.original.skills;
  const skillNames = useMemo(() => skills?.map((skill) => skill.name) || [], [skills]);
  const containerRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(skillNames.length);

  const container = containerRef.current;
  const containerWidth = container?.offsetWidth ? container.offsetWidth - 8 : 0; // Учитываем padding

  useEffect(() => {
    if (!containerRef.current || skillNames.length === 0 || !containerWidth) {
      setVisibleCount(skillNames.length);
      return;
    }

    // Примерная ширина элемента "+N" (около 40px)
    const plusElementWidth = 40;
    const gap = 4; // gap между элементами

    let totalWidth = 0;
    let count = 0;

    // Создаем временный элемент для измерения ширины каждого навыка
    const measureElement = document.createElement("span");
    measureElement.style.fontSize = "12px";
    measureElement.style.fontWeight = "600";
    measureElement.style.padding = "2px 10px";
    measureElement.style.border = "1px solid #e1e7ef";
    measureElement.style.borderRadius = "9999px";
    measureElement.style.visibility = "hidden";
    measureElement.style.position = "absolute";
    measureElement.style.whiteSpace = "nowrap";
    document.body.appendChild(measureElement);

    for (let i = 0; i < skillNames.length; i++) {
      measureElement.textContent = skillNames[i];
      const skillWidth = measureElement.offsetWidth;

      const willNeedPlusElement = i < skillNames.length - 1;
      const requiredWidth = totalWidth + skillWidth + (willNeedPlusElement ? gap + plusElementWidth : 0);

      if (requiredWidth <= containerWidth) {
        totalWidth += skillWidth + (i > 0 ? gap : 0);
        count++;
      } else {
        break;
      }
    }

    document.body.removeChild(measureElement);

    // Если не все навыки помещаются, уменьшаем количество на 1 для места под "+N"
    if (count < skillNames.length && count > 0) {
      count = Math.max(1, count - 1);
    }

    setVisibleCount(count);
  }, [skillNames, containerRef.current?.offsetWidth, containerWidth]);

  const visibleSkills = skillNames.slice(0, visibleCount);
  const hiddenSkills = skillNames.slice(visibleCount);
  const hasHiddenSkills = hiddenSkills.length > 0;

  return (
    <Flex ref={containerRef} alignItems="center" gap="2px" flexWrap="nowrap" width="100%" overflow="hidden" minHeight="20px" maxWidth="15vw">
      {visibleSkills.map((skill) => (
        <Text
          key={skill}
          fontSize={12}
          fontWeight={600}
          lineHeight="16px"
          padding="2px 8px"
          borderRadius="9999px"
          border="1px solid #e1e7ef"
          whiteSpace="nowrap"
          flexShrink={0}
        >
          {skill}
        </Text>
      ))}
      {hasHiddenSkills && (
        <Tooltip
          label={
            <Flex flexDirection="column" gap="2px">
              {hiddenSkills.map((skill) => (
                <Text key={skill} fontSize={12}>
                  {skill}
                </Text>
              ))}
            </Flex>
          }
          placement="top"
          hasArrow
          bg="gray.700"
          color="white"
          borderRadius="6px"
          p={2}
        >
          <Text
            fontSize={12}
            fontWeight={600}
            lineHeight="16px"
            padding="2px 10px"
            borderRadius="9999px"
            border="1px solid #e1e7ef"
            bg="#f7fafc"
            color="#4a5568"
            cursor="pointer"
            whiteSpace="nowrap"
            flexShrink={0}
            _hover={{
              bg: "#edf2f7",
              borderColor: "#cbd5e0",
            }}
          >
            +{hiddenSkills.length}
          </Text>
        </Tooltip>
      )}
    </Flex>
  );
};

export const CandidatePage = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();

  const { candidates, isLoading } = useAppSelector((state: RootState) => state.candidates);
  const { guides } = useAppSelector((state: RootState) => state.guides);

  const [channels, setChannels] = useState<{ value: string; label: string }[]>([]);
  const [skillList, setSkillList] = useState<{ value: string; label: string }[]>([]);
  const [sourceList, setSourceList] = useState<{ value: string; label: string }[]>([]);
  const [statusList, setStatusList] = useState<{ value: string; label: string }[]>([]);
  const [recruiters, setRecruiters] = useState<Recruiter[]>([]);
  const [totalItems, setTotalItems] = useState(0);

  const { sortColumn, sortDirection, handleSort } = useSorting("candidate.created_at");

  const { isOpen: isFiltersOpen, onOpen: onFiltersOpen, onClose: onFiltersClose } = useDisclosure();

  // Используем универсальный хук для работы с фильтрами
  const { formik, handleSearch, handleApplyFilters, handleResetFilters } = useSessionStorageFilters<CandidateFilters>({
    storageKey: "candidatePageFilters",
    defaultValues: defaultCandidateFilterFields,
  });

  const resetPagination = useCallback(() => {
    const limitValue = formik.values.limit || "20";
    const params = new URLSearchParams(searchParams);
    params.set("page", "1");
    params.set("limit", String(limitValue));
    navigate(`?${params.toString()}`, { replace: true });
    formik.setFieldValue("offset", "0");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, navigate]);

  const handleCreate = () => {
    navigate("create");
  };

  const appliedFiltersCount = useMemo(() => {
    let count = 0;
    if (formik.values.department_id) count += 1;
    if (formik.values.skill) count += 1;
    if (formik.values.source_id) count += 1;
    if (formik.values.status) count += 1;
    if (formik.values.created_by) count += 1;
    if (formik.values.created_at_from) count += 1;
    if (formik.values.created_at_to) count += 1;
    if (formik.values.responsible_recruiter_id) count += 1;
    return count;
  }, [
    formik.values.department_id,
    formik.values.skill,
    formik.values.source_id,
    formik.values.status,
    formik.values.created_by,
    formik.values.created_at_from,
    formik.values.created_at_to,
    formik.values.responsible_recruiter_id,
  ]);

  const refreshCandidates = useCallback(() => {
    const page = Number(searchParams.get("page")) || 1;
    const limit = Number(formik.values.limit) || 20;
    const offset = (page - 1) * limit;

    dispatch(
      fetchAllCandidates({
        limit,
        offset,
        search: formik.values.search || undefined,
        sort_column: sortColumn || undefined,
        sort_direction: sortDirection || undefined,
        channel_id: formik.values.channel_id ? Number(formik.values.channel_id) : undefined,
        department_id: formik.values.department_id ? Number(formik.values.department_id) : undefined,
        skill: formik.values.skill ? formik.values.skill.split(",").filter(Boolean) : undefined,
        source_id: formik.values.source_id ? Number(formik.values.source_id) : undefined,
        status: formik.values.status || undefined,
        created_by: formik.values.created_by ? Number(formik.values.created_by) : undefined,
        created_at_from: formik.values.created_at_from ? String(Math.floor(new Date(formik.values.created_at_from).getTime() / 1000)) : undefined,
        created_at_to: formik.values.created_at_to ? String(Math.floor(new Date(formik.values.created_at_to).getTime() / 1000)) : undefined,
        responsible_recruiter_id: formik.values.responsible_recruiter_id ? Number(formik.values.responsible_recruiter_id) : undefined,
      }),
    ).then((res: any) => {
      setTotalItems(res.payload.count);
    });
  }, [dispatch, formik.values, sortColumn, sortDirection, searchParams]);

  useEffect(() => {
    refreshCandidates();
  }, [formik.values, sortColumn, sortDirection, refreshCandidates]);

  useEffect(() => {
    const values = formik.values;
    updateRouteQuery(
      {
        search: values.search || "",
        channel_id: values.channel_id || "",
        department_id: values.department_id || "",
        skill: values.skill || "",
        source_id: values.source_id || "",
        status: values.status || "",
        created_by: values.created_by || "",
        responsible_recruiter_id: values.responsible_recruiter_id || "",
        created_at_from: values.created_at_from || "",
        created_at_to: values.created_at_to || "",
        sort_column: values.sort_column || "",
        sort_direction: values.sort_direction || "",
        limit: values.limit || "",
        offset: values.offset || "",
      },
      { replace: true },
    );
  }, [formik.values]);

  useEffect(() => {
    (async () => {
      const { data } = await api.channel.getChannels();
      const channelsData = data.map((channel) => ({
        value: String(channel.id),
        label: channel.name,
      }));
      setChannels(channelsData);

      const recruitersData = await api.recruiters.recruiters({});
      setRecruiters(recruitersData.data);
    })();
  }, []);

  useEffect(() => {
    setSkillList(transformGuides(guides?.Skill) || []);
    setSourceList(transformGuides(guides?.CandidateSource) || []);
    setStatusList(transformGuides(guides?.CandidateStatus) || []);
  }, [guides]);

  const columns = useMemo<Array<ColumnDef<ISingleCandidate, any>>>(
    () => [
      columnHelper.accessor("last_name", {
        id: "name",
        header: "ФИО",
        cell: (info) => {
          const candidate = info.row.original;
          const fullName = `${candidate.last_name ?? ""} ${candidate.first_name ?? ""} ${candidate.patronymic_name ?? ""}`.trim();
          const isAutoCreated = isAutoCreatedCandidate(candidate);

          const content = (
            <Flex alignItems="center" gap="4px">
              {isAutoCreated && <WarningIcon color="orange.500" boxSize="14px" />}
              <Text maxW="100px" noOfLines={1} fontSize={14} fontWeight={600}>
                {fullName}
              </Text>
            </Flex>
          );

          const tooltipLabel = isAutoCreated
            ? `${fullName}\n\nКандидат создан автоматически с сайта. Требуется проверка и заполнение данных`
            : fullName;

          return (
            <Tooltip label={tooltipLabel} hasArrow placement="top" whiteSpace="pre-line">
              {content}
            </Tooltip>
          );
        },
        enableColumnFilter: true,
        size: 120,
        minSize: 80,
      }),
      columnHelper.accessor("expected_position", {
        id: "expected_position",
        header: "Должность",
        cell: (info) => (
          <Tooltip label={info.getValue() || ""} hasArrow>
            <Text noOfLines={1} maxW="160px">
              {info.getValue()}
            </Text>
          </Tooltip>
        ),
        enableColumnFilter: true,
        size: 160,
        minSize: 120,
      }),
      columnHelper.accessor("skills", {
        id: "skills",
        header: () => (
          <Text w="100%" maxWidth="12vw">
            Ключевые навыки
          </Text>
        ),
        cell: (info) => SkillsCell(info),
        enableColumnFilter: true,
        filterFn: skillsSearchingFn,
        meta: {
          type: "array",
        },
        size: 120,
      }),
      columnHelper.accessor("contact_telegram", {
        id: "contact_telegram",
        header: "Телеграмм",
        cell: (info) => (
          <Tooltip label={info.getValue() || ""} hasArrow>
            <Code background="none" color="#65758b" whiteSpace="wrap" wordBreak="break-word">
              {info.getValue()}
            </Code>
          </Tooltip>
        ),
        enableColumnFilter: true,
        size: 140,
        minSize: 100,
      }),
      columnHelper.accessor("source.name", {
        id: "source_name",
        header: "Источник",
        cell: (info) => (
          <Tooltip label={info.getValue() || ""} hasArrow>
            <Text noOfLines={1} maxW="80px">
              {info.getValue()}
            </Text>
          </Tooltip>
        ),
        enableColumnFilter: true,
        size: 80,
        minSize: 40,
      }),
      columnHelper.accessor("status.name", {
        id: "status_name",
        header: "Статус кандидата",
        cell: (info) => StatusCell(info.getValue()),
        enableColumnFilter: true,
        size: 120,
        minSize: 80,
      }),
      columnHelper.accessor("created_by.username", {
        header: "Кем создано",
        cell: (info) => (
          <Tooltip label={info.getValue() || ""} hasArrow>
            <Text noOfLines={1} maxW="120px">
              {info.getValue()}
            </Text>
          </Tooltip>
        ),
        enableColumnFilter: true,
        size: 120,
        minSize: 80,
      }),
      columnHelper.accessor("created_at", {
        header: "Дата создания",
        cell: (info) => (
          <Tooltip label={formatDate(info.getValue() || "")} hasArrow>
            <Text noOfLines={1} maxW="100px">
              {formatDate(info.getValue() || "")}
            </Text>
          </Tooltip>
        ),
        enableColumnFilter: true,
        enableSorting: true,
        size: 100,
        minSize: 80,
      }),
    ],
    [],
  );

  return (
    <Layout>
      <Flex flexDirection="column" maxH="90vh" overflowY="auto" gap="24px">
        <Flex flexDirection="column">
          <Text fontSize={30} fontWeight={700} color="#3b515e">
            Кандидаты
          </Text>
          <Text fontSize={16} fontWeight={400} color="#65758b">
            Управление базой кандидатов
          </Text>
        </Flex>

        <BaseTableFilters
          handleSearch={handleSearch}
          placeHolderInput="Поиск по имени, должности или навыкам..."
          searchValue={formik.values.search}
          selectedFiltersCount={appliedFiltersCount}
          isShowCreateButton={true}
          onClickCreate={handleCreate}
          isLoading={isLoading}
          isOpenPopupFilters={isFiltersOpen}
          onClosePopupFilters={onFiltersClose}
          onClickBtnPopupFilters={onFiltersOpen}
          popupFilters={
            <FiltersPopover
              recruiters={recruiters}
              skillList={skillList}
              sourceList={sourceList}
              statusList={statusList}
              values={{
                department_id: formik.values.department_id || "",
                skill: formik.values.skill || "",
                source_id: formik.values.source_id || "",
                status: formik.values.status || "",
                created_by: formik.values.created_by || "",
                responsible_recruiter_id: formik.values.responsible_recruiter_id || "",
                created_at_from: formik.values.created_at_from || "",
                created_at_to: formik.values.created_at_to || "",
              }}
              onApply={(submitted) => {
                handleApplyFilters(submitted);
                onFiltersClose();
              }}
              onReset={() => {
                handleResetFilters();
              }}
            />
          }
        >
          <Flex gap="8px" alignItems="center">
            <BaseSelect
              options={channels}
              placeholder="Выберите канал"
              value={formik.values.channel_id || null}
              onChange={(val) => {
                formik.setFieldValue("channel_id", val ? String(val) : "");
                formik.setFieldValue("department_id", "");
                resetPagination();
              }}
              containerStyles={{ width: "300px", zIndex: 1000 }}
              isSearchable={false}
            />
          </Flex>
        </BaseTableFilters>

        <BaseTable
          list={candidates}
          columns={columns}
          clickHandler={(id) => {
            navigate(`${id}`);
          }}
          sortHandler={({ id, direction }) => {
            handleSort({ id, direction: direction ?? null });
            formik.setValues({
              ...formik.values,
              sort_column: `candidate.${id}`,
              sort_direction: direction as string,
            });
            resetPagination();
          }}
          sortState={{
            column: sortColumn || "",
            direction: sortDirection,
          }}
          isLoading={isLoading}
          rowStyleFn={(candidate) => {
            const isAutoCreated = isAutoCreatedCandidate(candidate);
            return isAutoCreated
              ? {
                  backgroundColor: "#FED7AA", // Рыжий/оранжевый цвет (orange-200 в Tailwind)
                }
              : {};
          }}
        />

        <Pagination
          isLoading={isLoading}
          totalItems={totalItems}
          onPageChange={(offset, limit) => {
            const params = new URLSearchParams(searchParams);
            params.set("page", String(offset / limit + 1));
            params.set("limit", String(limit));
            navigate(`?${params.toString()}`);
            formik.setValues({
              ...formik.values,
              offset: String(offset),
              limit: String(limit),
            });
          }}
        />
      </Flex>
    </Layout>
  );
};
